import React, { useCallback, useEffect, useState } from 'react';
import useForm from '@utils/useForm';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { isEmpty, get, find } from 'lodash';
import { GAAviva } from '@utils/gaAviva';
import { useHistory } from 'react-router-dom';
import { PaymentForm } from '@models/DPI/Payment.model';
import { Coverages, SQSPocketResponse } from '@models/SQSPocketRequest.model';
import { PaymentRequest } from '@models/PaymentRequest.model';
import { updateReferenceNo, updatePolicyNo, updateCardHolderName, updateBankId, updateOtherBank } from '@redux/slices/DPI/step5Slice';
import { getPolicyNo, postPayment, savePayment, sendMail } from '@api/index';
import { validate } from './service';
import { setLoadingState } from '@redux/slices/loadingSlice';
import { css } from '@linaria/core';
import { Helpers } from '@utils/helpers';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import StickyBox from '@components/StickyBox';
import PremiumCost from '@components/PremiumCost';
import InformationBox from '@components/InformationBox';
import Text from '@components/Text';
import ContainerBlock from '@components/ContainerBlock';
import Input from '@components/Input';
import Dropdown from '@components/Dropdown';
import Checkbox from '@components/Checkbox';
import CheckboxList from '@components/CheckboxList';
import Link from '@components/Link';
import Button from '@components/Button';
import ContactBox from '@components/ContactBox';
import Modal from '@components/Modal';
import Quote from '@components/Quote';
import Bullet from '@components/Bullet';
import Divider from '@components/Divider';


const BoldText = styled.span<{color: string}>`
  font-weight: 600;
  color: ${prop => prop.color};
`

const fluidBox = css`
  margin-top: -40px;
`;

const quoteInitialSample = [
  {
    name: 'Basic plan',
    details: [
      { text: 'Sum assured', value: '-' },
      { text: 'Premium term', value: '-' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Add-on',
    details: [
      { text: 'Rider', value: '-' },
      { text: 'Sum assured', value: '-' },
      { text: 'Premium term', value: '-' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Other details',
    details: [
      { text: 'Premium payment frequency', value: '-' },
      { text: 'Promo code', value: '-' },
    ]
  }
];

const trackOnload = ({ payment = '', errorMessage = '', policyID = '', sumassured = '' }): void => {
  const GA = GAAviva.getInstance();
  let { page, acquisition, form } = GA.digitalData;
  page.errorMessage = errorMessage;
  acquisition.product.policyID = policyID;
  if (!isEmpty(payment)) {
    acquisition.insurance.paymentMethod = payment;
    acquisition.insurance.sumassured = sumassured;
    acquisition.insurance.paymentOptions = 'Credit Card';
  }
  form.name = 'Payment';
  GA.trackPageLoad({ page, acquisition, form });
}


const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

export interface PaymentProps { }

function Payment(props: PaymentProps) {
  const theme: ITheme = useTheme();
  const history = useHistory();
  const dispatch = useAvivaDispatch();

  const aviaWebsite = get(window, 'AVIVA_VARIABLE_CONFIG.avivaWebsiteUrl');

  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const banks = useAvivaSelector(state => state.staticData.banks);
  const personalDetails = useAvivaSelector(state => state.dpiStep2.personalDetails);
  const sqsQuotation = useAvivaSelector(state => state.dpiStep1.sqsQuotation);
  const quoteRawResponse = useAvivaSelector(state => state.dpiStep1.quoteRawResponse);
  const referenceNo = useAvivaSelector(state => state.dpiStep5.referenceNo);
  const basicDetails = useAvivaSelector(state => state.dpiStep1.basicDetails);
  const occupation = useAvivaSelector(state => state.dpiStep1.occupation);
  const retrieveMyInfo = useAvivaSelector(state => state.dpiStep2.retrievedMyInfo);
  const isStep4Completed = useAvivaSelector(state => state.dpiStep4.isCompleted);
  const policyIllustrationPDFPath = useAvivaSelector(state => state.dpiStep4.pdfPath);
  // const cardHolderName = useAvivaSelector(state => state.dpiStep5.cardHolderName);
  const bankId = useAvivaSelector(state => state.dpiStep5.bankId);
  const otherBank = useAvivaSelector(state => state.dpiStep5.otherBank);

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [hasError, setError] = useState<boolean>(false);
  const [isShow, setIsShow] = useState<boolean>(false);
  const [premium, setPremium] = useState<any>({});
  const [quotes, setQuotes] = useState<any>(quoteInitialSample);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isStatusCheck, setIsStatusCheck] = useState<boolean>(false);

  const [isPaymentClick, setIsPaymentClick] = useState<boolean>(false);

  const transactionId= useAvivaSelector(state=>state.dpiStep1.transactionId)

  const { values, errors, handleChange, formValid } = useForm(new PaymentForm({
    no: get(personalDetails, 'nric', null), name: get(personalDetails, 'fullName', null)
  }), () => { }, validate);

  const setLoading = useCallback((payload: boolean) => {
    dispatch(setLoadingState(payload));
  }, [dispatch])

  const onSavePayment = useCallback(async (contractCode: string, paymentPayload: any, policyNo?: string) => {
    const quotation: SQSPocketResponse = new SQSPocketResponse(quoteRawResponse);
    const lifeDirect: Coverages | undefined = find(quotation.coverages, { id: 'lifeDirect' });
    const criticalIllness: Coverages | undefined = find(quotation.coverages, { id: 'lifeDirect_CIAB' });
    // const totalPayablePremium = Number(quotation.dpiPremium.totalPremium.value.toString().replace('S$', '')) || 1;
    const totalPayablePremium = (quotation.totalAftDisOriginalPremiumAmt) ? Helpers.toTwoDecimal(quotation.totalAftDisOriginalPremiumAmt) : Helpers.toTwoDecimal(quotation.totalOriginalPremiumAmt);
    console.log('CHECK', values['bank'], Number(values['bank']));
    const payload = {
      policy: {
        // "policyNumber": policyNo,
        "contractCode": contractCode,
        "customerId": retrieveMyInfo?.id,
        "productId": 2,
        "planName": lifeDirect?.name,
        "sumAssured": lifeDirect?.attributes?.sumAssured || 0,
        "premiumTerm": lifeDirect?.attributes?.premiumTerm,
        "premiumAmount": lifeDirect?.attributes?.modalPremium || 0,
        "riderCICover": criticalIllness?.name,
        "sumAssuredCICover": criticalIllness?.attributes?.sumAssured || 0,
        "premiumTermCICover": criticalIllness?.attributes?.premiumTerm,
        "premiumAmountCICover": criticalIllness?.attributes?.modalPremium || 0,
        "riderPACover": '',
        "sumAssuredPACover": 0,
        "premiumTermPACover": "",
        "premiumAmountPACover": 0,
        "premiumPaymentFrequency": lifeDirect?.attributes?.planPremiumMode,
        "totalAnnualPremium": quotation.totalOriginalPremiumAmt || 0,
        "afterPromoDiscount": quotation.totalOriginalPremiumAmt || 0,
        "firstYearDiscount": 0,
        "perpetualDiscount": 0,
        "discountType": '',
        "addONCoverage": '',
        "promoCode": '',
        "campaignCode": '',
        "productCode": 'DPI',
        "quotationRespData": JSON.stringify(quoteRawResponse), //json str
        "sourceBusiness": '',
        "contractCurrency": 'SGD',
        "smokingStatus": basicDetails?.smoker,
        "occupationRespData": JSON.stringify(occupation),
        "policyIllustrationPDFPath": policyIllustrationPDFPath,
        "paymentMode": 'CC',
        "transactionId":transactionId
      },
      payment: {
        cardHolderNric: get(personalDetails, 'nric', null),
        cardHolderName: get(personalDetails, 'fullName', null),
        bankId: Number(values['bank']),
        paymentAmount: totalPayablePremium,
        // referenceNo: referenceNo, //reference no
        otherBank: values['others']
      }
    }
    try {
      dispatch(setLoadingState(true));
      const response = await savePayment(payload);
      const refNo = get(response, 'data', '');
      console.log('refno', refNo);
      console.log('savePayment', refNo);
      paymentPayload.referenceNumber = refNo;
      dispatch(updateReferenceNo(String(refNo)));

      postPayment(paymentPayload).then((res: any) => {
        console.debug('postPayment', res);
        const paymentUrl = get(res, 'data');

        console.log(paymentUrl.split('?')[1]);
        // const params = new URLSearchParams(paymentUrl.split('?')[1]);
        // const refNo = params.get('referenceNumber');

        dispatch(updateCardHolderName(values['name']));
        dispatch(updateBankId(Number(values['bank'])));
        dispatch(updateOtherBank(values['others']));

        const fullPath = window.location.origin + window.location.pathname.replace('/index.html', '') + '#/dpi/payment?status=processing';
        console.log(fullPath);
        setLoading(true);
        window.open(paymentUrl, '_self');
      }).catch((err: any) => {
        setLoading(false);
        console.error(err);
        setError(true);
      });

    } catch (error) {
      dispatch(setLoadingState(false));
    }
  }, [dispatch, values, basicDetails, occupation, quoteRawResponse, personalDetails, retrieveMyInfo, policyIllustrationPDFPath, setLoading])

  const onSuccessPayment = useCallback(async (payload: any) => {
    dispatch(setLoadingState(true));
    try {
      const quotation: SQSPocketResponse = new SQSPocketResponse(quoteRawResponse);
      const lifeDirect: Coverages | undefined = find(quotation.coverages, { id: 'lifeDirect' });
      const response = await getPolicyNo(payload, 2, retrieveMyInfo?.id);
      const policyNo = get(response, 'data');
      dispatch(updatePolicyNo(String(policyNo)));
      dispatch(setLoadingState(false));
      trackOnload({ policyID: policyNo, payment: Helpers.getBankName(`${bankId}`) || otherBank, sumassured: `${lifeDirect?.attributes?.sumAssured || 0}` });
      trackFormSubmmit();
      sendMail(payload, 2, retrieveMyInfo?.id);
      // await onSavePayment(policyNo);
      history.push('/dpi/thank-you');
    } catch (err) {
      trackFormSubmmit(get(err, 'message'));
      dispatch(setLoadingState(false));
      history.push('/dpi/thank-you');
    }
  }, [dispatch, history, bankId, otherBank, quoteRawResponse, retrieveMyInfo]);

  const onFailedPayment = useCallback(async (payload: any) => {
    // dispatch(setLoadingState(true));
    trackFormSubmmit('Payment is unsuccessful');
    dispatch(setLoadingState(false));
    // try {
    //   const response = await getPolicyNo(payload, 2, retrieveMyInfo?.id);
    //   const policyNo = get(response, 'data');
    //   dispatch(updatePolicyNo(String(policyNo)));
    //   dispatch(setLoadingState(false));
    //   // await onSavePayment(policyNo);
    //   trackFormSubmmit('Payment is unsuccessful');
    // } catch (err) {
    //   trackFormSubmmit('Payment is unsuccessful');
    //   dispatch(setLoadingState(false));
    // }
  }, [dispatch])

  const checkStatus = useCallback(() => {
    const params = new URLSearchParams(window.location.href.split('?')[1]);
    const status = params.get('status');
    console.log('data saved referenceNo', referenceNo, status);
    if (!isStatusCheck) {
      if (referenceNo !== '' && status === 'processing') {
        setIsProcessing(true);
        dispatch(setLoadingState(true));
        console.log('is payment success');
        onSuccessPayment(referenceNo);
      } else if (status === 'unsuccessful') {
        setError(true);
        trackOnload({ policyID: referenceNo, payment: Helpers.getBankName(`${bankId}`) || otherBank, errorMessage: 'Payment is unsuccessful' });
        onFailedPayment(referenceNo);
        setLoading(false);
      } else {
        trackOnload({});
        setLoading(false);
      }
      setIsStatusCheck(true);
      setLoading(false);
    }
  }, [referenceNo, onSuccessPayment, setError, setIsProcessing, setLoading, otherBank, onFailedPayment, isStatusCheck, dispatch, bankId])


  const onClickConfirmAndPay = () => {
    if (isPaymentClick) return;
    setIsPaymentClick(true);
    setTimeout(() => {
      setIsPaymentClick(false);
    }, 5000);

    setLoading(true);
    dispatch(setLoadingState(true));
    const pocket = new SQSPocketResponse(quoteRawResponse);
    const lifeDirect: Coverages | undefined = find(pocket.coverages, { id: 'lifeDirect' });
    // const totalPayablePremium = Number(pocket.dpiPremium.totalPremium.value.toString().replace('S$', '')) || 1; // for testing
    const totalPayablePremium = (pocket.totalAftDisOriginalPremiumAmt) ? Helpers.toTwoDecimal(pocket.totalAftDisOriginalPremiumAmt) : Helpers.toTwoDecimal(pocket.totalOriginalPremiumAmt);
    const payload = new PaymentRequest({ amount: totalPayablePremium, productName: 'DPI', contractCode: lifeDirect?.attributes?.productCode, transactionId });

    dispatch(updateCardHolderName(values['name']));
    dispatch(updateBankId(Number(values['bank'])));
    dispatch(updateOtherBank(values['others']));
    onSavePayment(lifeDirect?.attributes?.productCode || '', payload);

    // postPayment(payload).then((res: any) => {
    //   console.debug('postPayment', res);
    //   const paymentUrl = get(res, 'data');

    //   console.log(paymentUrl.split('?')[1]);
    //   const params = new URLSearchParams(paymentUrl.split('?')[1]);
    //   const refNo = params.get('referenceNumber');
    //   console.log('refno', refNo);
    //   dispatch(updateReferenceNo(String(refNo)));
    //   dispatch(updateCardHolderName(values['name']));
    //   dispatch(updateBankId(Number(values['bank'])));
    //   dispatch(updateOtherBank(values['others']));

    //   const fullPath = window.location.origin + window.location.pathname.replace('/index.html', '') + '#/dpi/payment?status=processing';
    //   console.log(fullPath);
    //   setLoading(false);
    //   window.open(paymentUrl, '_self');
    // }).catch((err: any) => {
    //   setLoading(false);
    //   console.error(err);
    //   setError(true);
    // });
  };

  const toggleModal = () => { setIsShow(!isShow) };
  const onEditQuote = () => {
    history.push('/dpi/coverage');
  };
  const onBack = () => {
    if (hasError) {
      setError(false);
    } else {
      history.push('/dpi/review');
    }
  };

  const gotoContact = () => {
    history.push('/dpi/contact');
  }

  const backToAviva = () => {
    const website = get(window, 'AVIVA_VARIABLE_CONFIG.avivaWebsiteUrl');
    window.open(website, '_self');
  }


  useEffect(() => {
    if (!isStep4Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
      history.push('/dpi/declaration');
    }
    if (!isPageLoaded) {
      const quotation: SQSPocketResponse = new SQSPocketResponse(sqsQuotation);
      console.log(quoteRawResponse, quotation);
      const pocket = new SQSPocketResponse(quoteRawResponse);
      setPremium(pocket.dpiPremium);
      setQuotes(pocket.dpiQuote);
      checkStatus();
      setIsPageLoaded(true);
    }

  }, [sqsQuotation, quoteRawResponse, checkStatus, isPageLoaded, setIsPageLoaded, isStep4Completed, history]);



  return (
    <Container>
      {isProcessing && (
        <ContainerBlock className={fluidBox} background={theme.colors.secondary} padding="40px 0" paddingTablet="130px 0 110px 30px">
          <ContainerGrid>
            <FullGrid>
              <LeftGrid>
                <Heading fontWeight={600} color={theme.colors.white} margin="0 0 10px">Thank you! </Heading>
                <Text fontSize={theme.fontSize.label} fontWeight={300} color={theme.colors.white}>Your application has been submitted. We’ll notify you on the status of your application within 5 working days.</Text>
              </LeftGrid>
            </FullGrid>
          </ContainerGrid>
        </ContainerBlock>
      )}
      <ContainerGrid>
        {!hasError ?
          (
            <React.Fragment>
              {!isProcessing && (<>
                <FullGrid>
                  <MultiSteps margin="0 0 20px" steps={5} step={5} label="Payment" />
                  <StickyBox>
                    <PremiumCost {...premium} />
                  </StickyBox>
                  <Heading margin="0 0 30px"> Premium payment </Heading>
                </FullGrid>
                <LeftGrid>


                  <InformationBox hasClose>
                    <Text>The payment mode for the initial premium will apply to subsequent premiums.</Text>
                  </InformationBox>
                  <ContainerBlock background={theme.colors.grey[200]} padding="30px 20px 20px" paddingTablet={30}>
                    <Heading variant="sub">Credit Card payment details</Heading>
                    <Divider size={1} margin="0 0 30px" />
                    <Text margin="0 0 30px" fontSize={theme.fontSize.smallTitle} fontWeight={300}>
                      Total amount outstanding: <BoldText color={theme.colors.grey[300]}>{premium?.totalPremium?.value}</BoldText>
                    </Text>
                    <Heading variant="label">Card holder&apos;s name</Heading>
                    {!errors?.name?.isValid && errors?.name?.message && <Bullet variant="error">{errors?.name?.message}</Bullet>}
                    <Input margin="0 0 30px" name="name" value={values['name']} invalid={!isEmpty(errors?.name?.message)} type="alphanumeric" maxLength={30} onChange={handleChange} />
                    <Heading variant="label">Card holder&apos;s NRIC/FIN</Heading>
                    <Input margin="0 0 30px" disabled name="no" value={values['no']} />
                    <Heading variant="label">Credit card issuing bank</Heading>
                    <Dropdown source={banks} bindLabel="description" bindId="code" name="bank" value={values['bank']} onChange={handleChange} placeholder="Please select" margin="0 0 30px" />
                    {values['bank'] === '11' && (
                      <React.Fragment>
                        <Heading variant="label"> Others </Heading>
                        {!errors.others.isValid && errors.others.message && <Bullet variant="error">{errors.others.message}</Bullet>}
                        <Input name="others" invalid={!isEmpty(errors.others.message)} value={values['others']} onChange={handleChange} maxLength={30} />
                      </React.Fragment>
                    )}
                    <CheckboxList margin={0}>
                      <Checkbox checked={values['isAuthorised']} name="isAuthorised" onChange={handleChange}><Text>I authorise Singapore Life Ltd. to charge the <b>initial and subsequent</b> premium(s) to my credit card account for this insurance policy.</Text></Checkbox>
                    </CheckboxList>
                  </ContainerBlock>
                  <InformationBox hasClose margin="0 0 40px">
                    <Text fontWeight={600}>Please ensure you&apos;ve provided your own credit card details.</Text>
                    <Text>If you’re unable to make payment using your own credit card, please contact us <Link variant="button" onClick={gotoContact} text="here" /></Text>
                  </InformationBox>
                  <Button margin="0 0 20px" label="Confirm and pay" disabled={!formValid} onClick={onClickConfirmAndPay} />
                  <Button margin="0 0 40px" label="Back" variant='secondary' onClick={onBack} />
                </LeftGrid>
                <EmptyGrid></EmptyGrid>
                <RightGrid>
                  <Quote onEdit={toggleModal} details={quotes} note="*Premium payment term is equal to the policy term" />
                  <ContactBox items={contactInformation} />
                </RightGrid>
              </>)}
            </React.Fragment>
          ) :
          (
            <React.Fragment>
              <LeftGrid>
                <InformationBox hasClose variant="error">
                  <Text>Payment is unsuccessful</Text>
                </InformationBox>
                <Heading margin="0 0 30px">Oops...</Heading>
                <Heading margin="0 0 30px" variant="label">There seems to be a problem with your payment, and your purchase is incomplete.</Heading>
                <Heading margin="0 0 30px" variant="label">You may wish to check your credit card details or select another payment option.</Heading>
                <Text margin="0 0 40px">
                  Alternatively, you can contact our Customer Service Centre at <Link text="+65 6827 9933" />, Mondays to Fridays between 8.45am and 5.30pm for assistance.
                </Text>
                <Button margin="0 0 20px" label="Try again" onClick={onBack} />
                <Button margin="0 0 40px" label="Back to website" variant='secondary' onClick={backToAviva} />
              </LeftGrid>
              <EmptyGrid></EmptyGrid>
              <RightGrid>
                <ContactBox items={contactInformation} />
              </RightGrid>
            </React.Fragment>
          )
        }

        <Modal show={isShow} onClose={toggleModal}>
          <Heading fontWeight={600} variant="smallTitle" margin="0 0 20px" marginTablet="0 0 30px">Modify quote</Heading>
          <Text margin="0 0 27px">You will need to answer the underwriting questions once again if there is a change from the initial quotation.</Text>
          <Button margin="0 0 20px" label="Proceed" onClick={onEditQuote}></Button>
          <Button margin={0} label="Cancel" variant="secondary" onClick={toggleModal}></Button>
        </Modal>
      </ContainerGrid>
    </Container>
  );
};

export default Payment;
